<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import QRCode from 'qrcode';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  strategies: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete']);

const { t } = useI18n();

const showQrModal = ref(false);
const qrStrategy = ref(null);
const qrDataUrl = ref('');

const getFormattedText = strategy => {
  return strategy.text || '';
};

const copyToClipboard = async text => {
  try {
    await navigator.clipboard.writeText(text);
    useAlert(t('STRATEGIES.COPY_SUCCESS'));
  } catch (err) {
    console.error('Failed to copy:', err);
  }
};

const openQrCode = async strategy => {
  if (!strategy.short_link) {
    useAlert(t('STRATEGIES.QR_CODE.NO_LINK'));
    return;
  }
  qrStrategy.value = strategy;
  qrDataUrl.value = await QRCode.toDataURL(strategy.short_link, {
    width: 300,
    margin: 2,
    color: { dark: '#000000', light: '#FFFFFF' },
  });
  showQrModal.value = true;
};

const copyQrLink = async () => {
  await copyToClipboard(qrStrategy.value.short_link);
  useAlert(t('STRATEGIES.QR_CODE.LINK_COPIED'));
};

const downloadQrCode = () => {
  const link = document.createElement('a');
  link.download = `qrcode-${qrStrategy.value.name}.png`;
  link.href = qrDataUrl.value;
  link.click();
};
</script>

<template>
  <div class="bg-n-solid-2 rounded-xl border border-n-weak overflow-hidden">
    <!-- Header -->
    <div class="px-6 py-4 border-b border-n-weak bg-n-solid-3">
      <div class="flex items-center justify-between">
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('STRATEGIES.LIST_TITLE') }}
        </h2>
        <span class="text-sm text-n-slate-11">
          {{ t('STRATEGIES.TOTAL_COUNT', { count: strategies.length }) }}
        </span>
      </div>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="border-b border-n-weak bg-n-alpha-1">
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('STRATEGIES.TABLE.NAME') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('STRATEGIES.TABLE.TEXT') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('STRATEGIES.TABLE.SHORT_LINK') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('STRATEGIES.TABLE.GROUP_LINK') }}
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('STRATEGIES.TABLE.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr
            v-for="strategy in strategies"
            :key="strategy.id"
            class="hover:bg-n-alpha-1 transition-colors"
          >
            <td class="px-4 py-4">
              <span class="text-sm font-medium text-n-slate-12">
                {{ strategy.name }}
              </span>
            </td>
            <td class="px-4 py-4">
              <div class="flex items-center gap-2">
                <span class="text-sm text-n-slate-11 line-clamp-2 max-w-md">
                  {{ getFormattedText(strategy) }}
                </span>
                <button
                  v-if="strategy.text"
                  class="p-1 hover:bg-n-alpha-2 rounded flex-shrink-0"
                  :title="t('STRATEGIES.COPY_TEXT')"
                  @click="copyToClipboard(getFormattedText(strategy))"
                >
                  <span class="i-lucide-copy size-4 text-n-slate-11" />
                </button>
              </div>
            </td>
            <td class="px-4 py-4">
              <div v-if="strategy.short_link" class="flex items-center gap-2">
                <a
                  :href="strategy.short_link"
                  target="_blank"
                  class="text-sm text-n-brand hover:underline truncate max-w-xs"
                >
                  {{ strategy.short_link }}
                </a>
                <button
                  class="p-1 hover:bg-n-alpha-2 rounded"
                  @click="copyToClipboard(strategy.short_link)"
                >
                  <span class="i-lucide-copy size-4 text-n-slate-11" />
                </button>
              </div>
              <span v-else class="text-sm text-n-slate-10">-</span>
            </td>
            <td class="px-4 py-4">
              <div v-if="strategy.group_link" class="flex items-center gap-2">
                <a
                  :href="strategy.group_link"
                  target="_blank"
                  class="text-sm text-n-brand hover:underline truncate max-w-xs"
                >
                  {{ strategy.group_link }}
                </a>
                <button
                  class="p-1 hover:bg-n-alpha-2 rounded"
                  @click="copyToClipboard(strategy.group_link)"
                >
                  <span class="i-lucide-copy size-4 text-n-slate-11" />
                </button>
              </div>
              <span v-else class="text-sm text-n-slate-10">-</span>
            </td>
            <td class="px-4 py-4">
              <div class="flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  icon="i-lucide-qr-code"
                  :title="t('STRATEGIES.QR_CODE.TITLE')"
                  @click="openQrCode(strategy)"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  icon="i-lucide-pencil"
                  @click="emit('edit', strategy)"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  icon="i-lucide-trash-2"
                  color-scheme="alert"
                  @click="emit('delete', strategy)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- QR Code Modal -->
    <div
      v-if="showQrModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="showQrModal = false"
    >
      <div class="w-[400px] rounded-xl bg-n-solid-2 p-6 shadow-xl">
        <h3 class="mb-1 text-base font-semibold text-n-slate-12">
          {{ t('STRATEGIES.QR_CODE.TITLE') }}
        </h3>
        <p class="mb-4 text-sm text-n-slate-11">
          {{ qrStrategy?.name }}
        </p>

        <div class="mb-4 flex justify-center">
          <img
            :src="qrDataUrl"
            alt="QR Code"
            class="rounded-lg"
          >
        </div>

        <p class="mb-4 break-all text-center text-xs text-n-slate-10">
          {{ qrStrategy?.short_link }}
        </p>

        <div class="flex justify-center gap-2">
          <Button
            icon="i-lucide-download"
            color-scheme="primary"
            size="sm"
            @click="downloadQrCode"
          >
            {{ t('STRATEGIES.QR_CODE.DOWNLOAD') }}
          </Button>
          <Button
            icon="i-lucide-link"
            variant="ghost"
            size="sm"
            @click="copyQrLink"
          >
            {{ t('STRATEGIES.QR_CODE.COPY_LINK') }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
